// ═══════════════════════════════════════════════════════════════════════
// SalonPriveGate — Introductory screen for /configurer
// Accès direct sans code (friction supprimée pour dirigeants 40-60 ans).
// L'engagement est pris sur le Manifesto (bouton "Je m'engage à l'excellence").
// Persisté dans localStorage sous "sabril_private_unlock"
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Instagram, Lock, ChevronLeft } from 'lucide-react';
import { DA, MimosaBranch } from '../theme/sabril-da';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const SABRIL_AVATAR_URL = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/xf03ot6r_sabril.png';
const INSTAGRAM_URL = 'https://www.instagram.com/sabril.j/';
const UNLOCK_FLAG = 'unlocked';
const STORAGE_KEY = 'sabril_private_unlock';
// Code d'accès Étape 2 — transmis par Sabril à chaque client après visio #2.
// Case-insensitive (comparaison en majuscule).
const ACCESS_CODE = 'SABRIL-EXPRESS-2026';

export function isSalonPriveUnlocked() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === UNLOCK_FLAG;
  } catch (_e) {
    return false;
  }
}

export default function SalonPriveGate({ onUnlocked }) {
  const [showWelcome, setShowWelcome] = useState(false);
  const [code, setCode] = useState('');
  const navigate = useNavigate();

  const attemptUnlock = () => {
    const normalized = code.trim().toUpperCase();
    if (normalized !== ACCESS_CODE) {
      toast.error("Code d'accès invalide", {
        description: 'Ce code vous est transmis par Sabril à la fin de votre 2ᵉ visio.',
      });
      try { navigator.vibrate?.([20, 40, 20]); } catch (_e) { /* ignore */ }
      return;
    }
    try { localStorage.setItem(STORAGE_KEY, UNLOCK_FLAG); } catch (_e) { /* ignore */ }
    try { navigator.vibrate?.(16); } catch (_e) { /* ignore */ }
    toast.success('Bienvenue dans le Salon Privé.');
    onUnlocked?.();
  };

  const handleEnter = () => {
    try { navigator.vibrate?.(18); } catch (_e) { /* ignore */ }
    setShowWelcome(true);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // WELCOME SCREEN — Hermès-style private atelier entrance
  // ═══════════════════════════════════════════════════════════════════════
  if (showWelcome) {
    return (
      <div
        className="min-h-screen relative overflow-hidden"
        style={{
          background: `radial-gradient(ellipse at top, ${DA.paper} 0%, ${DA.ivoryLight} 45%, ${DA.ivory} 100%)`,
        }}
        data-testid="salon-prive-welcome"
      >
        {/* Editorial gold ornaments — very subtle, decorative only */}
        <div style={{ position: 'absolute', top: -30, left: -40, opacity: 0.20, pointerEvents: 'none' }}>
          <MimosaBranch size={240} color={DA.goldMain} />
        </div>
        <div style={{ position: 'absolute', bottom: -40, right: -50, opacity: 0.18, pointerEvents: 'none', transform: 'rotate(180deg)' }}>
          <MimosaBranch size={220} color={DA.goldMain} />
        </div>

        <div
          className="relative z-10 max-w-md mx-auto px-8 py-16 flex flex-col min-h-screen"
          style={{ justifyContent: 'space-between' }}
        >
          <div>
            {/* Fine gold ornament frame — top */}
            <div className="flex items-center justify-center gap-3 mb-12" data-testid="welcome-topframe">
              <span style={{ flex: 1, height: 0.5, background: `linear-gradient(90deg, transparent 0%, ${DA.goldMain} 100%)` }} />
              <span
                style={{
                  width: 8, height: 8, borderRadius: 999,
                  background: DA.goldMain,
                  boxShadow: '0 0 0 3px rgba(216,148,64,0.15)',
                }}
                aria-hidden
              />
              <span style={{ flex: 1, height: 0.5, background: `linear-gradient(90deg, ${DA.goldMain} 0%, transparent 100%)` }} />
            </div>

            {/* Overline */}
            <p style={{
              fontFamily: DA.mono, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.38em', textTransform: 'uppercase',
              color: DA.goldDeep, marginBottom: 24, textAlign: 'center',
            }}>
              Salon Privé · Sabril
            </p>

            {/* Cathedral headline — very large, italic serif */}
            <h1 style={{
              fontFamily: DA.serif,
              fontSize: 'clamp(46px, 12vw, 78px)', fontWeight: 400,
              letterSpacing: '-0.04em',
              color: DA.greenDeep, lineHeight: 0.95,
              textAlign: 'center', marginBottom: 4,
              fontStyle: 'italic',
            }}>
              L&apos;excellence.
            </h1>
            <p style={{
              fontFamily: DA.mono, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.42em', textTransform: 'uppercase',
              color: DA.inkFaint, textAlign: 'center', marginBottom: 40,
            }}>
              — Rien d&apos;autre —
            </p>

            {/* Manifesto — feutré, aspirationnel, pas confrontationnel */}
            <div className="mb-10" style={{ maxWidth: 340, margin: '0 auto' }}>
              <p style={{
                fontFamily: DA.serif, fontSize: 16, fontWeight: 500,
                color: DA.inkBody, lineHeight: 1.7,
                letterSpacing: '-0.005em', textAlign: 'center', marginBottom: 20,
              }}>
                Ici, nous ne travaillons pas la moyenne.<br/>
                Nous cultivons <em style={{ color: DA.greenDeep, fontWeight: 700 }}>la précision</em>,
                <br/>
                <em style={{ color: DA.greenDeep, fontWeight: 700 }}>la discipline</em>, et
                <em style={{ color: DA.greenDeep, fontWeight: 700 }}> la beauté du geste juste</em>.
              </p>

              {/* Fine gold divider */}
              <div className="flex justify-center my-8">
                <span style={{ width: 28, height: 1, background: DA.goldMain, opacity: 0.6 }} />
              </div>

              <p style={{
                fontFamily: DA.serif, fontSize: 15, fontWeight: 500,
                color: DA.inkBody, lineHeight: 1.7,
                letterSpacing: '-0.005em', textAlign: 'center',
                fontStyle: 'italic',
              }}>
                Ceux qui franchissent cette porte<br/>
                viennent chercher <strong style={{ color: DA.goldDeep, fontStyle: 'normal', fontWeight: 800 }}>l&apos;exigence</strong>.<br/>
                Ils repartent avec <strong style={{ color: DA.goldDeep, fontStyle: 'normal', fontWeight: 800 }}>l&apos;excellence</strong>.
              </p>
            </div>
          </div>

          {/* CTAs — bottom-anchored, refined */}
          <div className="flex flex-col gap-3" style={{ maxWidth: 340, margin: '0 auto', width: '100%' }}>
            {/* Fine gold ornament — small dot */}
            <div className="flex justify-center mb-2">
              <span
                style={{
                  width: 6, height: 6, borderRadius: 999,
                  background: DA.goldMain, opacity: 0.5,
                }}
                aria-hidden
              />
            </div>

            {/* CODE D'ACCÈS · Étape 2 · transmis par Sabril après visio #2 */}
            <div style={{ marginBottom: 6 }}>
              <label
                htmlFor="gate-access-code"
                style={{
                  display: 'block',
                  fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                  letterSpacing: '0.32em', textTransform: 'uppercase',
                  color: DA.goldDeep, marginBottom: 8, textAlign: 'center',
                }}
              >
                Code d&apos;accès · Étape 2
              </label>
              <input
                id="gate-access-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') attemptUnlock(); }}
                placeholder="Ex : SABRIL-EXPRESS-2026"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.85)',
                  color: DA.greenDeep,
                  border: `0.5px solid ${DA.goldMain}`,
                  borderRadius: 4,
                  fontFamily: DA.mono, fontSize: 13, fontWeight: 700,
                  letterSpacing: '0.14em', textTransform: 'uppercase',
                  textAlign: 'center',
                  padding: '13px 14px',
                  outline: 'none',
                  boxShadow: 'inset 0 1px 3px rgba(11,43,36,0.08)',
                }}
                data-testid="gate-code-input"
              />
              <p style={{
                fontFamily: DA.serif, fontSize: 11, fontStyle: 'italic',
                color: DA.inkMuted, marginTop: 8, textAlign: 'center', lineHeight: 1.45,
              }}>
                Ce code vous est transmis personnellement<br/>
                à la fin de votre 2<sup>ᵉ</sup> visio avec Sabril.
              </p>
            </div>

            <button
              type="button"
              onClick={attemptUnlock}
              className="w-full flex flex-col items-center justify-center py-4 transition-all active:scale-[0.995]"
              style={{
                background: DA.greenDeep,
                color: DA.ivory,
                border: `0.5px solid ${DA.goldMain}`,
                borderRadius: 4, // sharp corners = Hermès sharpness
                fontFamily: DA.serif, fontWeight: 500,
                cursor: 'pointer',
                boxShadow: '0 22px 44px -18px rgba(11,43,36,0.35), inset 0 0 0 3px rgba(216,148,64,0.10)',
                padding: '20px 24px',
              }}
              data-testid="welcome-enter-btn"
            >
              <span
                style={{
                  fontFamily: DA.serif, fontSize: 15.5, fontWeight: 500,
                  letterSpacing: '0.02em', color: DA.ivory,
                  fontStyle: 'italic',
                }}
              >
                Débloquer le Salon Privé
              </span>
              <span style={{
                fontFamily: DA.mono, fontSize: 8.5, fontWeight: 700,
                letterSpacing: '0.36em', textTransform: 'uppercase',
                color: DA.goldMain, marginTop: 8,
              }}>
                ⋅ Entrer ⋅
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
                navigate('/prsnl');
              }}
              className="w-full py-2 transition-colors active:opacity-70"
              style={{
                background: 'transparent',
                color: DA.inkMuted,
                border: 'none',
                fontFamily: DA.serif, fontSize: 12.5, fontWeight: 500,
                letterSpacing: '-0.005em', fontStyle: 'italic',
                cursor: 'pointer',
              }}
              data-testid="welcome-back-btn"
            >
              Ce n&apos;est pas mon moment · Retour
            </button>
          </div>

          {/* Signature */}
          <div className="text-center mt-10">
            <p style={{
              fontFamily: DA.serif, fontSize: 13, fontStyle: 'italic',
              color: DA.inkMuted, lineHeight: 1.5, letterSpacing: '-0.005em',
            }}>
              — Sabril Jurczeszyn
            </p>
            <p style={{
              marginTop: 4,
              fontFamily: DA.mono, fontSize: 8.5, fontWeight: 700,
              letterSpacing: '0.34em', textTransform: 'uppercase',
              color: DA.inkFaint,
            }}>
              Directeur associé
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${DA.ivory} 0%, ${DA.ivoryLight} 100%)`,
      }}
      data-testid="salon-prive-gate"
    >
      {/* Editorial ornaments */}
      <div style={{ position: 'absolute', top: -20, left: -20, opacity: 0.5, pointerEvents: 'none' }}>
        <MimosaBranch size={220} color={DA.goldMain} />
      </div>
      <div style={{ position: 'absolute', bottom: -30, right: -30, opacity: 0.4, pointerEvents: 'none', transform: 'rotate(180deg)' }}>
        <MimosaBranch size={200} color={DA.goldMain} />
      </div>

      <div className="relative z-10 max-w-md mx-auto px-6 py-10">
        {/* Retour */}
        <button
          type="button"
          onClick={() => navigate('/prsnl')}
          className="inline-flex items-center gap-1.5 mb-8"
          style={{
            fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: DA.inkMuted, fontWeight: 700, background: 'transparent', border: 'none', cursor: 'pointer',
          }}
          data-testid="gate-back"
        >
          <ChevronLeft size={12} strokeWidth={2.2} />
          Retour /prsnl
        </button>

        {/* Avatar + Overline */}
        <div className="flex flex-col items-center text-center mb-6">
          <div
            style={{
              position: 'relative',
              width: 96, height: 96, borderRadius: 999,
              padding: 3,
              background: `linear-gradient(135deg, ${DA.goldMain} 0%, ${DA.goldSoft} 100%)`,
              boxShadow: `0 20px 40px -18px rgba(216,148,64,0.6), 0 0 0 4px ${DA.paper}`,
              marginBottom: 14,
            }}
          >
            <img
              src={SABRIL_AVATAR_URL}
              alt="Sabril Jurczeszyn"
              style={{ width: '100%', height: '100%', borderRadius: 999, objectFit: 'cover' }}
            />
            <span
              style={{
                position: 'absolute', bottom: -4, right: -4,
                width: 30, height: 30, borderRadius: 999,
                background: DA.greenDeep, color: DA.goldMain,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: `2px solid ${DA.paper}`,
              }}
              aria-hidden
            >
              <Lock size={14} strokeWidth={2.4} />
            </span>
          </div>

          <p
            style={{
              fontFamily: DA.mono, fontSize: 10, fontWeight: 800,
              letterSpacing: '0.32em', textTransform: 'uppercase',
              color: DA.goldDeep, marginBottom: 6,
            }}
          >
            Salon Privé · Bienvenue
          </p>

          <h1
            style={{
              fontFamily: DA.serif, fontSize: 'clamp(30px, 6vw, 42px)', fontWeight: 900,
              letterSpacing: '-0.03em', color: DA.greenDeep, lineHeight: 1.02, marginBottom: 12,
            }}
          >
            Salon Privé
            <span style={{ display: 'block', color: DA.goldDeep, fontStyle: 'italic', marginTop: 2 }}>
              de Sabril.
            </span>
          </h1>

          <p
            style={{
              fontFamily: DA.sans, fontSize: 14.5, color: DA.inkBody,
              lineHeight: 1.6, letterSpacing: '-0.005em', maxWidth: 360,
            }}
          >
            Restaurant, hôtel, beach club.<br/>
            Vous avez terminé vos <strong style={{ color: DA.greenDeep }}>2 visios avec Sabril</strong>&nbsp;? Débloquez le Salon Privé et configurez votre accompagnement sur-mesure — <strong style={{ color: DA.greenDeep }}>les 790€ déjà déduits</strong>.
          </p>
        </div>

        {/* Direct entry — primary CTA, no friction */}
        <button
          type="button"
          onClick={handleEnter}
          className="w-full flex flex-col items-center justify-center transition-all active:scale-[0.99] mb-4"
          style={{
            background: DA.goldMain,
            color: DA.greenDeep,
            border: `0.5px solid ${DA.goldDeep}`,
            borderRadius: 14,
            cursor: 'pointer',
            boxShadow: '0 18px 36px -14px rgba(216,148,64,0.60), inset 0 1px 0 rgba(255,255,255,0.35)',
            padding: '20px 22px',
          }}
          data-testid="gate-enter-btn"
        >
          <span style={{
            fontFamily: DA.serif, fontSize: 16, fontWeight: 700,
            letterSpacing: '-0.005em', color: DA.greenDeep,
          }}>
            Débloquer avec mon code
          </span>
          <span style={{
            fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: DA.greenDeep, marginTop: 7, opacity: 0.75,
          }}>
            ⋅ Étape 2 · Accès sur code ⋅
          </span>
        </button>

        {/* Separator */}
        <div className="flex items-center gap-3 my-4">
          <span style={{ flex: 1, height: 0.5, background: DA.goldSoft }} />
          <span style={{
            fontFamily: DA.mono, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: DA.inkFaint,
          }}>
            Une question&nbsp;?
          </span>
          <span style={{ flex: 1, height: 0.5, background: DA.goldSoft }} />
        </div>

        {/* Instagram CTA — secondary contact */}
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2.5 py-3 transition-all active:scale-[0.99]"
          style={{
            background: 'transparent',
            border: `0.5px solid ${DA.goldSoft}`,
            borderRadius: 12,
            color: DA.greenDeep,
            fontFamily: DA.sans, fontSize: 13, fontWeight: 700, letterSpacing: '-0.005em',
            textDecoration: 'none',
          }}
          data-testid="gate-instagram-cta"
        >
          <Instagram size={16} strokeWidth={2.2} style={{ color: DA.goldDeep }} />
          <span>Me contacter en direct — @sabril.j</span>
        </a>

        {/* Footer note */}
        <p
          style={{
            marginTop: 20,
            fontFamily: DA.sans, fontSize: 11.5, color: DA.inkMuted,
            textAlign: 'center', lineHeight: 1.6, fontStyle: 'italic',
          }}
        >
          Je ne prends que quelques dirigeants par trimestre pour bien faire mon travail.<br/>
          On regarde ce que je propose. Ensuite on décide ensemble.
        </p>
      </div>
    </div>
  );
}
